if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // A deliberate gallery gesture takes precedence over a previous view-button choice.
        this.elements.viewer?.addEventListener('pointerdown', () => { this.auraUserMedia = null; }, { passive: true });
        this.elements.viewer?.addEventListener('keydown', () => { this.auraUserMedia = null; });
        this.elements.viewer?.addEventListener('wheel', () => { this.auraUserMedia = null; }, { passive: true });
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.selectMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        const data = this.querySelector('[data-aura-gallery-map]');
        const product = this.closest('product-info[id^="MainProduct-"]');
        if (!data || !product || this.auraGroups) return;
        const map = JSON.parse(data.textContent);
        const anchors = new Set(map.variants.map(v => String(v.media)).filter(id => id !== 'null'));
        if (anchors.size < 1) return;
        const excludedMedia = new Set((map.excluded || []).map(String).filter(id => !anchors.has(id)));
        // Older duplicate uploads must not become alternate views of an adjacent colour.
        const imageKey = src => (src || '').split('?')[0].split('/').pop().replace(/_[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?=\.)/ig, '');
        const assignedSources = new Set(map.variants.filter(v => v.media).map(v => imageKey(v.image)).filter(Boolean));
        const duplicateMedia = new Set();
        this.querySelectorAll('li[data-media-id]').forEach(item => {
          const id = item.dataset.mediaId.split('-').pop();
          if (!anchors.has(id) && assignedSources.has(imageKey(item.querySelector('img')?.getAttribute('src')))) duplicateMedia.add(id);
        });
        // Explicit product--colour--view filenames form a group independent of gallery order.
        // Keep the legacy adjacency rule only for legacy anchors; held/shared originals
        // must not be inherited as alternate views of a newly replaced colour.
        const familyKey = src => imageKey(src).match(/^(\d+--.+)--(?:front|back)--\d+x\d+\./i)?.[1] || null;
        const families = new Map();
        map.variants.forEach(v => { if (v.media) families.set(String(v.media), familyKey(v.image)); });
        this.querySelectorAll('li[data-media-id]').forEach(item => {
          const id = item.dataset.mediaId.split('-').pop();
          if (!families.has(id)) families.set(id, familyKey(item.querySelector('img')?.getAttribute('src')));
        });
        const familyOwners = new Map();
        anchors.forEach(id => { const family = families.get(id); if (family) familyOwners.set(family, id); });
        const groups = new Map();
        let owner = null;
        map.media.forEach(id => {
          id = String(id);
          if (anchors.has(id)) owner = id;
          const family = families.get(id);
          groups.set(id, anchors.has(id) ? id : family ? familyOwners.get(family) || null : families.get(owner) ? null : owner);
        });
        this.auraMediaOwners = groups;
        let frame = 0, lastColour = null;
        const sync = () => {
          frame = 0;
          const swatch = product.querySelector('.g-variant-color-detail input:checked');
          const nativeGroup = [...product.querySelectorAll('variant-selects fieldset')].find(g => /colou?r/i.test(g.querySelector('legend')?.textContent || ''));
          const initial = map.variants.find(v => v.id === map.selected);
          const colour = swatch?.value || nativeGroup?.querySelector('input:checked')?.value || initial?.options[map.colourIndex];
          const variants = map.variants.filter(v => v.options[map.colourIndex] === colour && v.media);
          if (!variants.length) return;
          const allowed = new Set(variants.map(v => String(v.media)));
          // Unassigned leading catalogue images are not alternate views of every colour.
          const visible = id => !excludedMedia.has(id) && !duplicateMedia.has(id) && allowed.has(groups.get(id));
          this.querySelectorAll('li[data-media-id],li[data-target]').forEach(item => {
            const id = (item.dataset.mediaId || item.dataset.target).split('-').pop();
            const value = String(visible(id));
            if (item.dataset.auraColourVisible !== value) item.dataset.auraColourVisible = value;
            if (value === 'true') {
              const image = item.querySelector('img');
              if (image) { image.loading = 'eager'; image.decoding = 'async'; }
            }
          });
          const active = this.elements.viewer.querySelector('.is-active[data-media-id]');
          const changed = lastColour !== colour;
          if (changed && lastColour !== null) this.auraUserMedia = null;
          lastColour = colour;
          if (changed || !active || !visible(active.dataset.mediaId.split('-').pop())) {
            const item = [...this.elements.viewer.querySelectorAll('[data-media-id]')].find(el => el.dataset.mediaId.endsWith('-' + variants[0].media));
            if (item) this.setActiveMedia(item.dataset.mediaId, false);
          }
          // Keep the zoom modal on the same selected-colour media as PDP and quick selection.
          const modal = document.getElementById('ProductModal-' + product.dataset.section);
          modal?.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach(item => {
            const value = String(visible(item.dataset.mediaId.split('-').pop()));
            if (item.dataset.auraColourVisible !== value) item.dataset.auraColourVisible = value;
          });
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
          this.restoreExplicitView();
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
        const observer = new MutationObserver(records => {
          if (records.some(r => r.type === 'childList' || r.target.matches?.('li[data-media-id],li[data-target]'))) schedule();
        });
        observer.observe(this, { childList:true, subtree:true, attributes:true, attributeFilter:['class','style'] });
        product.addEventListener('change', schedule);
        product.addEventListener('click', schedule);
        this.auraGroups = { observer, product, schedule, cancel: () => cancelAnimationFrame(frame) };
        sync();
      }

      disconnectedCallback() {
        if (!this.auraGroups) return;
        const {observer,product,schedule,cancel} = this.auraGroups;
        observer.disconnect(); cancel();
        product.removeEventListener('change', schedule);
        product.removeEventListener('click', schedule);
        this.auraGroups = null;
      }

      restoreExplicitView() {
        if (!this.auraUserMedia) return false;
        const chosen = this.elements.viewer.querySelector(`[data-media-id="${this.auraUserMedia}"]`);
        if (!chosen || chosen.dataset.auraColourVisible === 'false' || !chosen.classList.contains('is-active')) return false;
        const track = chosen.parentElement;
        if (!this.mql.matches && Math.abs(track.scrollLeft - chosen.offsetLeft) > 2) {
          track.scrollTo({ left: chosen.offsetLeft, behavior: 'instant' });
        }
        const thumb = this.elements.thumbnails?.querySelector(`[data-target="${this.auraUserMedia}"]`);
        if (thumb) this.setActiveThumbnail(thumb);
        return true;
      }

      onSlideChanged(event) {
        if (this.restoreExplicitView()) return;
        if (!this.isConnected || !this.elements.thumbnails || !event.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // Explicit view choices survive a delayed variant response for the same colour.
      selectMedia(mediaId) {
        this.auraUserMedia = mediaId;
        this.setActiveMedia(mediaId, false, true);
      }

      setActiveMedia(mediaId, prepend, preserveViewport = false) {
        if (prepend && this.auraUserMedia && this.auraMediaOwners) {
          const owner = id => this.auraMediaOwners.get(String(id).split('-').pop());
          const chosen = this.elements.viewer.querySelector(`[data-media-id="${this.auraUserMedia}"]`);
          if (chosen && owner(mediaId) && owner(mediaId) === owner(this.auraUserMedia)) {
            mediaId = this.auraUserMedia;
            preserveViewport = true;
          } else {
            this.auraUserMedia = null;
          }
        }
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          return;
        }
        // Decode the requested view before hiding the current one. Only the selected
        // colour's small image group is eager; other colours stay lazy.
        const image = activeMedia.querySelector('img');
        if (image && !image.complete && typeof image.decode === 'function') {
          if (this.auraPendingMedia === mediaId) return;
          this.auraPendingMedia = mediaId;
          image.loading = 'eager';
          image.decode().catch(() => {}).then(() => {
            if (!this.isConnected || this.auraPendingMedia !== mediaId) return;
            this.auraPendingMedia = null;
            if (image.complete && image.naturalWidth) this.setActiveMedia(mediaId, prepend, preserveViewport);
          });
          return;
        }
        this.auraPendingMedia = null;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // GLO groups use catalogue order; selecting a variant must not reorder it.
        if (prepend && !this.closest('product-info[id^="MainProduct-"]')) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (preserveViewport) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (typeof this.elements.thumbnails.isSlideVisible !== 'function' || !this.elements.thumbnails.slider || this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
